export type SSGElement = HTMLElement | SVGElement | DocumentFragment

export type AriaAttributes = 'label' | 'labelledby'

export type StyleKeys = Omit<
  keyof CSSStyleDeclaration,
  'length' | 'parentRule' | 'item' | 'getPropertyPriority' | 'getPropertyValue' | 'removeProperty' | 'setProperty'
> &
  string

export interface ItemOptions {
  arias?: { [key in AriaAttributes]?: string }
  attrs?: Record<string, string>
  classes?: string[]
  dataset?: Record<string, string>
  styles?: Partial<Record<StyleKeys, string>>
  children?: Array<SSGElement>
  text?: string
  options?: {
    isHTML?: boolean
    isSVG?: boolean
    isVue?: boolean
  }
}

export type SVGItemOptions = ItemOptions & {
  options: { isSVG: true }
}

export type HTMLItemOptions = ItemOptions & {
  options?: { isSVG?: false }
}

export function b(item: 'fragment', document: Document, params: { children: Array<SSGElement> }): DocumentFragment
export function b<K extends keyof SVGElementTagNameMap>(
  item: K,
  document: Document,
  params: SVGItemOptions,
): SVGElementTagNameMap[K]
export function b<K extends keyof HTMLElementTagNameMap>(
  item: K,
  document: Document,
  params?: HTMLItemOptions,
): HTMLElementTagNameMap[K]
export function b(item: string, document: Document, params?: ItemOptions): SSGElement {
  if (item === 'fragment') {
    const fragment = document.createDocumentFragment()
    params?.children?.forEach((child) => {
      fragment.appendChild(child as SSGElement)
    })
    return fragment
  }

  const { attrs, styles, text, children, options } = params || {}

  const isSVG = options?.isSVG === true

  const itemBuilt: HTMLElement | SVGElement = isSVG
    ? document.createElementNS('http://www.w3.org/2000/svg', item)
    : document.createElement(item)

  if (params?.arias) {
    for (const ariaKey in params.arias) {
      const ariaValue = params.arias[ariaKey as AriaAttributes]
      if (ariaValue !== undefined) itemBuilt.setAttribute(`aria-${ariaKey}`, ariaValue)
    }
  }

  if (attrs) {
    for (const attrsKey in attrs) {
      itemBuilt.setAttribute(attrsKey, attrs[attrsKey])
    }
  }

  if (styles && itemBuilt instanceof HTMLElement) {
    for (const styleKey in styles) {
      ;(itemBuilt.style as unknown as Record<StyleKeys, string>)[styleKey as StyleKeys] = styles[styleKey as StyleKeys]!
    }
  }

  if (text) {
    itemBuilt[options?.isHTML ? 'innerHTML' : 'textContent'] = text
  }

  if (children?.length) {
    children.forEach((child) => {
      itemBuilt.appendChild(child as SSGElement)
    })
  }

  if (params?.dataset) {
    for (const datasetKey in params.dataset) {
      itemBuilt.dataset[datasetKey] = params.dataset[datasetKey]
    }
  }

  if (params?.classes) {
    params.classes.forEach((className) => {
      itemBuilt.classList.add(className)
    })
  }

  return itemBuilt
}
